import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertCircle, CheckCircle, Paintbrush, Phone, ShoppingBag, FileText, Sparkles, DollarSign } from "lucide-react";
import { API } from "../constants";

const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [designRequests, setDesignRequests] = useState([]);
  const [callRequests, setCallRequests] = useState([]);
  const [conciergeQuotes, setConciergeQuotes] = useState([]);
  const [selectedView, setSelectedView] = useState('overview');
  const [workflows, setWorkflows] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    paidOrders: 0,
    pendingPayments: 0
  });

  useEffect(() => {
    fetchOrders();
    fetchDesignRequests();
    fetchCallRequests();
    fetchConciergeQuotes();
    fetchWorkflows();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      calculatePaymentAnalytics();
    }
  }, [orders]);

  const calculatePaymentAnalytics = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const totalRevenue = orders.reduce((sum, order) => 
      sum + (order.total || order.total_amount || 0), 0);
    
    const monthlyRevenue = orders
      .filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
      })
      .reduce((sum, order) => sum + (order.total || order.total_amount || 0), 0);
    
    const paidOrders = orders.filter(order => 
      order.payment_intent_id && 
      !['order_placed'].includes(order.status)).length;
    
    const pendingPayments = orders.filter(order => 
      order.status === 'order_placed' && 
      !order.payment_intent_id).length;
    
    setPaymentAnalytics({
      totalRevenue,
      monthlyRevenue,
      paidOrders,
      pendingPayments
    });
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchDesignRequests = async () => {
    try {
      const response = await axios.get(`${API}/design-requests`);
      setDesignRequests(response.data);
    } catch (error) {
      console.error('Error fetching design requests:', error);
    }
  };

  const fetchCallRequests = async () => {
    try {
      const response = await axios.get(`${API}/call-requests`);
      setCallRequests(response.data);
    } catch (error) {
      console.error('Error fetching call requests:', error);
    }
  };

  const fetchConciergeQuotes = async () => {
    try {
      const response = await axios.get(`${API}/concierge-quotes`);
      setConciergeQuotes(response.data);
    } catch (error) {
      console.error('Error fetching concierge quotes:', error);
    }
  };

  const updateOrderStatus = async (orderId, status, notes = '', trackingNumber = '') => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, {
        order_id: orderId,
        status: status,
        notes: notes,
        tracking_number: trackingNumber
      });
      fetchOrders(); // Refresh orders
      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'order_placed': 'bg-blue-100 text-blue-800',
      'art_received': 'bg-purple-100 text-purple-800',
      'art_proofed': 'bg-yellow-100 text-yellow-800',
      'art_approved': 'bg-green-100 text-green-800',
      'production_started': 'bg-orange-100 text-orange-800',
      'packaging': 'bg-indigo-100 text-indigo-800',
      'shipping': 'bg-cyan-100 text-cyan-800',
      'tracking_sent': 'bg-emerald-100 text-emerald-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (selectedOrder) {
    const workflow = workflows[selectedOrder.workflow_type]?.steps || [];
    
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Button 
            variant="outline" 
            onClick={() => setSelectedOrder(null)}
            className="mr-4"
          >
            ← Back to Admin
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            Manage Order #{selectedOrder.id.slice(-8).toUpperCase()}
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Update Order Status</CardTitle>
                <CardDescription>
                  {workflows[selectedOrder.workflow_type]?.name} - {selectedOrder.items.length} item(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workflow.map((step, index) => {
                    const isCompleted = workflow.findIndex(s => s.status === selectedOrder.status) >= index;
                    const isCurrent = selectedOrder.status === step.status;
                    
                    return (
                      <div key={step.status} className="flex items-center space-x-4 p-3 rounded-lg border">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                          isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {isCompleted ? '✓' : index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-semibold ${isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
                            {step.name}
                          </h4>
                          <p className="text-sm text-gray-600">{step.description}</p>
                        </div>
                        <Button
                          size="sm"
                          variant={isCurrent ? "default" : "outline"}
                          onClick={() => updateOrderStatus(selectedOrder.id, step.status)}
                          disabled={isCompleted && !isCurrent}
                        >
                          {isCurrent ? 'Current' : isCompleted ? 'Completed' : 'Set Status'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold">Customer</Label>
                  <p className="text-sm">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.customer_email}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-semibold">Total</Label>
                  <p className="text-2xl font-bold">${(selectedOrder.total || selectedOrder.total_amount || 0).toFixed(2)}</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold">Order Date</Label>
                  <p className="text-sm">{formatDate(selectedOrder.created_at)}</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold">Add Tracking Number</Label>
                  <Input
                    placeholder="Enter tracking number"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        updateOrderStatus(selectedOrder.id, selectedOrder.status, '', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const renderKanbanBoard = () => {
    // Define kanban columns
    const columns = [
      { id: 'lead', title: 'Leads', color: 'bg-yellow-100 border-yellow-300' },
      { id: 'quote_sent', title: 'Quote Sent', color: 'bg-blue-100 border-blue-300' },
      { id: 'paid', title: 'Paid', color: 'bg-green-100 border-green-300' },
      { id: 'production', title: 'Production', color: 'bg-orange-100 border-orange-300' },
      { id: 'shipped', title: 'Shipped', color: 'bg-purple-100 border-purple-300' }
    ];

    // Combine all items (orders, call requests, design requests)
    const allItems = [
      ...orders.map(item => ({ ...item, type: 'order' })),
      ...callRequests.map(item => ({ ...item, type: 'call_request', status: item.status || 'lead' })),
      ...designRequests.map(item => ({ ...item, type: 'design_request', status: item.status || 'lead' }))
    ];

    // Group items by status
    const groupedItems = columns.reduce((acc, column) => {
      acc[column.id] = allItems.filter(item => {
        // Map different statuses to kanban columns
        const statusMapping = {
          'order_placed': 'lead',
          'payment_received': 'paid',
          'production_start': 'production',
          'shipped': 'shipped',
          'pending': 'lead',
          'quote_sent': 'quote_sent',
          'in_progress': 'production',
          'completed': 'shipped'
        };
        return statusMapping[item.status] === column.id;
      });
      return acc;
    }, {});

    const getItemIcon = (type) => {
      switch (type) {
        case 'order': return <ShoppingBag className="w-4 h-4" />;
        case 'call_request': return <Phone className="w-4 h-4" />;
        case 'design_request': return <Paintbrush className="w-4 h-4" />;
        default: return <FileText className="w-4 h-4" />;
      }
    };

    const handleStatusChange = async (itemId, newStatus, itemType) => {
      try {
        const endpoint = itemType === 'order' ? 'orders' : itemType === 'call_request' ? 'call-requests' : 'design-requests';
        await axios.patch(`${API}/${endpoint}/${itemId}`, { status: newStatus });
        
        // Refresh data
        if (itemType === 'order') fetchOrders();
        else if (itemType === 'call_request') fetchCallRequests();
        else if (itemType === 'design_request') fetchDesignRequests();
        
        toast.success('Status updated successfully');
      } catch (error) {
        toast.error('Failed to update status');
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Kanban Board</h2>
          <div className="flex space-x-4 text-sm text-gray-600">
            <span>Total: {allItems.length} items</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 overflow-x-auto">
          {columns.map((column) => (
            <div key={column.id} className={`border-2 rounded-lg ${column.color} min-h-96`}>
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-lg">{column.title}</h3>
                <p className="text-sm text-gray-600">{groupedItems[column.id]?.length || 0} items</p>
              </div>
              
              <div className="p-2 space-y-2 max-h-80 overflow-y-auto">
                {groupedItems[column.id]?.map((item) => (
                  <div key={`${item.type}-${item.id}`} className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getItemIcon(item.type)}
                        <span className="font-medium text-sm">
                          {item.customer_name || item.customer_email || 'Unknown Customer'}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {item.type.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>ID: #{item.id.slice(-8).toUpperCase()}</p>
                      {item.total && (
                        <p className="font-semibold text-green-600">${item.total.toFixed(2)}</p>
                      )}
                      {item.project_title && (
                        <p className="font-medium">{item.project_title}</p>
                      )}
                      <p>{formatDate(item.created_at)}</p>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="mt-3 flex space-x-1">
                      {columns.map((targetColumn) => (
                        targetColumn.id !== column.id && (
                          <Button
                            key={targetColumn.id}
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1"
                            onClick={() => handleStatusChange(item.id, targetColumn.id, item.type)}
                          >
                            → {targetColumn.title}
                          </Button>
                        )
                      ))}
                    </div>
                  </div>
                ))}
                
                {groupedItems[column.id]?.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <p className="text-sm">No items in {column.title.toLowerCase()}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {columns.map((column) => (
            <div key={column.id} className="text-center">
              <div className="text-2xl font-bold">{groupedItems[column.id]?.length || 0}</div>
              <div className="text-sm text-gray-600">{column.title}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOrderManagement = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Order Management</h2>
          <div className="flex items-center space-x-3">
            <Badge variant="secondary">{orders.length} total orders</Badge>
            <Badge className="bg-green-100 text-green-800">{paymentAnalytics.paidOrders} paid</Badge>
            <Badge className="bg-orange-100 text-orange-800">{paymentAnalytics.pendingPayments} pending</Badge>
          </div>
        </div>
      
      <div className="grid gap-4">
        {orders.map((order) => {
          const workflow = workflows[order.workflow_type]?.steps || [];
          const currentIndex = workflow.findIndex(step => step.status === order.status);
          const nextStep = workflow[currentIndex + 1];
          
          return (
            <Card key={order.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <h3 className="text-lg font-semibold">
                        #{order.id.slice(-8).toUpperCase()}
                      </h3>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <Badge variant="outline">
                        {workflows[order.workflow_type]?.name || order.workflow_type}
                      </Badge>
                      {order.payment_intent_id ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Paid
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Payment Pending
                        </Badge>
                      )}
                    </div>
                    <div className="grid md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">Customer: </span>
                        <span className="font-medium">{order.customer_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Email: </span>
                        <span className="font-medium">{order.customer_email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Items: </span>
                        <span className="font-medium">{order.items.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total: </span>
                        <span className="font-medium">${(order.total || order.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Ordered: {formatDate(order.created_at)}
                      {order.tracking_number && (
                        <span className="ml-4">
                          Tracking: <span className="font-mono">{order.tracking_number}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {nextStep && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order.id, nextStep.status);
                        }}
                      >
                        Advance to {nextStep.name}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => setSelectedOrder(order)}
                    >
                      Manage Order
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {orders.length === 0 && (
          <div className="text-center py-12 text-gray-500">No orders yet</div>
        )}
      </div>
    </div>
  );
  };

  // Guest order lookup removed per user request

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>

      <Tabs value={selectedView} onValueChange={setSelectedView} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="kanban">Kanban Board</TabsTrigger>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="call-requests">Calls ({callRequests.length})</TabsTrigger>
          <TabsTrigger value="design-requests">Design ({designRequests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Enhanced Financial Overview */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${paymentAnalytics.totalRevenue.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {orders.length} total orders
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
                  This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  ${paymentAnalytics.monthlyRevenue.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Current month revenue
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Paid Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{paymentAnalytics.paidOrders}</div>
                <p className="text-xs text-muted-foreground">
                  Successfully completed
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2 text-orange-600" />
                  Pending Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {paymentAnalytics.pendingPayments}
                </div>
                <p className="text-xs text-muted-foreground">
                  Require attention
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Secondary Metrics */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Call Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{callRequests.length}</div>
                <p className="text-xs text-muted-foreground">
                  {callRequests.filter(r => r.status === 'pending').length} pending
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Call Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{callRequests.length}</div>
                <p className="text-xs text-muted-foreground">
                  {callRequests.filter(r => r.status === 'pending').length} pending
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Design Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{designRequests.length}</div>
                <p className="text-xs text-muted-foreground">
                  {designRequests.filter(r => r.status === 'pending').length} pending
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Concierge Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{conciergeQuotes.length}</div>
                <p className="text-xs text-muted-foreground">
                  {conciergeQuotes.filter(q => q.status === 'pending').length} pending
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">#{order.id.slice(-8).toUpperCase()}</p>
                        <p className="text-sm text-gray-600">{order.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${(order.total || order.total_amount || 0).toFixed(2)}</p>
                        <Badge className={getStatusColor(order.status)} size="sm">
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Recent Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...callRequests, ...designRequests].slice(0, 5).map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{request.customer_name}</p>
                        <p className="text-sm text-gray-600">
                          {request.project_title || 'Call Request'}
                        </p>
                      </div>
                      <Badge variant="outline" size="sm">
                        {request.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="kanban" className="space-y-6">
          {renderKanbanBoard()}
        </TabsContent>

        <TabsContent value="orders" className="space-y-4">
          {renderOrderManagement()}
        </TabsContent>

        <TabsContent value="call-requests" className="space-y-4">
          <div className="grid gap-4">
            {callRequests.map((request) => (
              <Card key={request.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {request.customer_name}
                    </h3>
                    <p className="text-gray-600">{request.customer_email} - {request.phone}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </p>
                    <div className="mt-2">
                      <Badge className="bg-orange-100 text-orange-800">{request.status}</Badge>
                      {request.preferred_time && (
                        <Badge className="ml-2 bg-blue-100 text-blue-800">
                          Prefers: {request.preferred_time}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">${request.total_estimated_price?.toFixed(2) || 'TBD'}</p>
                    <p className="text-sm text-gray-600">{request.items.length} items</p>
                  </div>
                </div>
              </Card>
            ))}
            {callRequests.length === 0 && (
              <div className="text-center py-12 text-gray-500">No call requests yet</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="design-requests" className="space-y-4">
          <div className="grid gap-4">
            {designRequests.map((request) => (
              <Card key={request.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{request.project_title}</h3>
                    <p className="text-gray-600">{request.customer_name} - {request.customer_email}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </p>
                    <div className="mt-2">
                      <Badge className="bg-purple-100 text-purple-800">{request.project_type}</Badge>
                      <Badge className="ml-2 bg-gray-100 text-gray-800">{request.status}</Badge>
                      {request.budget_range && (
                        <Badge className="ml-2 bg-green-100 text-green-800">{request.budget_range}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            {designRequests.length === 0 && (
              <div className="text-center py-12 text-gray-500">No design requests yet</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="space-y-6">
          <div className="grid gap-6">
            {/* Payment Analytics Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Payment Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Revenue Breakdown</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total Revenue:</span>
                        <span className="font-bold text-green-600">${paymentAnalytics.totalRevenue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>This Month:</span>
                        <span className="font-bold">${paymentAnalytics.monthlyRevenue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Average Order:</span>
                        <span className="font-medium">
                          ${orders.length > 0 ? (paymentAnalytics.totalRevenue / orders.length).toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Payment Status</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Paid Orders:</span>
                        <span className="font-bold text-green-600">{paymentAnalytics.paidOrders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pending Payments:</span>
                        <span className="font-bold text-orange-600">{paymentAnalytics.pendingPayments}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payment Rate:</span>
                        <span className="font-medium">
                          {orders.length > 0 ? ((paymentAnalytics.paidOrders / orders.length) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Payment Details */}
            <Card>
              <CardHeader>
                <CardTitle>Order Payments</CardTitle>
                <CardDescription>Detailed payment information for all orders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <p className="font-semibold">#{order.id.slice(-8).toUpperCase()}</p>
                            <p className="text-sm text-gray-600">{order.customer_name} - {order.customer_email}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Badge className={getStatusColor(order.status)}>
                              {order.status.replace('_', ' ')}
                            </Badge>
                            {order.payment_intent_id ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Paid
                              </Badge>
                            ) : (
                              <Badge className="bg-orange-100 text-orange-800">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Payment Pending
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(order.created_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">${(order.total || order.total_amount || 0).toFixed(2)}</p>
                        {order.payment_intent_id && (
                          <p className="text-xs text-gray-500">ID: {order.payment_intent_id.slice(-8)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {orders.length === 0 && (
                    <div className="text-center py-12 text-gray-500">No payment data available</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Concierge Quote Component

export { AdminDashboard };
export default AdminDashboard;
